import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  ['student', 'Student'], ['parent', 'Parent'], ['educator', 'Educator'],
  ['school_leader', 'School or district leader'], ['counselor', 'Counselor'],
  ['workforce_organization', 'Workforce organization'], ['community_partner', 'Community partner'],
  ['potential_sponsor', 'Potential sponsor'], ['other', 'Other'],
];

const utmFromUrl = () => {
  const p = new URLSearchParams(window.location.search);
  const out = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((k) => {
    if (p.get(k)) out[k] = p.get(k);
  });
  return out;
};

/** Reusable email capture. variant="inline" for hero/footer strips, "full" for the
 *  waitlist page and long-form CTAs. */
export default function EmailSignupForm({ variant = 'inline', source = 'website', heading, sub, className = '' }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', organization: '',
    role_interest: 'student', location: '', consent: false, message: '', honeypot: '',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.first_name.trim() || !form.email.trim()) return toast.error('Name and email, please.');
    if (!form.consent) return toast.error('Please tick the consent box so we can email you.');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/waitlist`, {
        ...form,
        source,
        source_page: window.location.pathname,
        campaign: utmFromUrl().utm_campaign || '',
        referral_url: document.referrer || '',
        utm: utmFromUrl(),
      });
      setDone(true);
      toast.success(r.data.message || "You're on the list.");
      axios.post(`${API}/analytics/event`, {
        event: 'newsletter_signup', path: window.location.pathname, utm: utmFromUrl(),
      }).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That didn't go through. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className={`rounded-2xl border border-cyan-500/30 bg-cyan-500/5 p-6 ${className}`} data-testid="signup-success">
        <CheckCircle2 className="w-6 h-6 text-cyan-300" />
        <h3 className="text-lg md:text-lg font-semibold text-white mt-2">You're on the list.</h3>
        <p className="text-sm text-slate-300 mt-1">
          Check your inbox for a welcome note. We'll send early-access news, new guides and invitations to
          demos, pilots and workshops, nothing else.
        </p>
      </div>
    );
  }

  const input = 'w-full bg-slate-900/80 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-300 focus:border-cyan-500/60 focus:outline-none transition-colors duration-200';

  if (variant === 'inline') {
    return (
      <form onSubmit={submit} className={`w-full ${className}`} data-testid="email-signup-inline">
        <div className="flex flex-col sm:flex-row gap-2">
          <input className={input} placeholder="First name" value={form.first_name}
            onChange={(e) => set('first_name', e.target.value)} data-testid="signup-first-name" />
          <input className={input} type="email" placeholder="you@example.com" value={form.email}
            onChange={(e) => set('email', e.target.value)} data-testid="signup-email" />
          <button type="submit" disabled={busy}
            className="btn-primary inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap disabled:opacity-50"
            data-testid="signup-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Join the waitlist
          </button>
        </div>
        <label className="flex items-start gap-2 mt-2 text-xs text-slate-300">
          <input type="checkbox" checked={form.consent} onChange={(e) => set('consent', e.target.checked)}
            className="mt-0.5" data-testid="signup-consent" />
          <span>Email me updates about Agentic FuturePath AI. Unsubscribe any time.</span>
        </label>
        <input tabIndex={-1} autoComplete="off" value={form.honeypot} onChange={(e) => set('honeypot', e.target.value)}
          className="hidden" aria-hidden="true" />
      </form>
    );
  }

  return (
    <form onSubmit={submit} className={`rounded-2xl border border-slate-700/80 bg-slate-900/50 p-6 ${className}`}
      data-testid="email-signup-full">
      {heading && <h3 className="font-outfit text-xl font-semibold text-white">{heading}</h3>}
      {sub && <p className="text-sm text-slate-300 mt-1 mb-4">{sub}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input className={input} placeholder="First name *" value={form.first_name}
          onChange={(e) => set('first_name', e.target.value)} data-testid="signup-first-name" />
        <input className={input} placeholder="Last name" value={form.last_name}
          onChange={(e) => set('last_name', e.target.value)} data-testid="signup-last-name" />
        <input className={`${input} sm:col-span-2`} type="email" placeholder="Email address *" value={form.email}
          onChange={(e) => set('email', e.target.value)} data-testid="signup-email" />
        <input className={input} placeholder="Organization (optional)" value={form.organization}
          onChange={(e) => set('organization', e.target.value)} data-testid="signup-organization" />
        <input className={input} placeholder="State or location (optional)" value={form.location}
          onChange={(e) => set('location', e.target.value)} data-testid="signup-location" />
        <select className={`${input} sm:col-span-2`} value={form.role_interest}
          onChange={(e) => set('role_interest', e.target.value)} data-testid="signup-role">
          {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <textarea className={`${input} sm:col-span-2`} rows={3} placeholder="Anything you'd like us to know? (optional)"
          value={form.message} onChange={(e) => set('message', e.target.value)} data-testid="signup-message" />
      </div>
      <label className="flex items-start gap-2 mt-3 text-xs text-slate-300">
        <input type="checkbox" checked={form.consent} onChange={(e) => set('consent', e.target.checked)}
          className="mt-0.5" data-testid="signup-consent" />
        <span>
          Yes, email me platform updates, early access news and invitations. I can unsubscribe at any time.
          See our <a href="/privacy" className="text-cyan-300 hover:text-cyan-200">privacy policy</a>.
        </span>
      </label>
      <input tabIndex={-1} autoComplete="off" value={form.honeypot} onChange={(e) => set('honeypot', e.target.value)}
        className="hidden" aria-hidden="true" />
      <button type="submit" disabled={busy}
        className="btn-primary inline-flex items-center gap-2 mt-4 px-6 py-3 rounded-full text-sm font-semibold disabled:opacity-50"
        data-testid="signup-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        Join the waitlist
      </button>
    </form>
  );
}
